import React, { useEffect, useState } from "react";
import api from "@/lib/api";

// Recherche fournisseurs réservée à l'équipe (BackOffice admin).
// Chaque recherche cible un seul fournisseur ; les erreurs sont affichées telles
// que l'API les qualifie (non configuré, identifiants refusés, délai dépassé…).

const SUPPLIERS = {
  ferebam: { label: "Ferebam", modes: ["immat", "reference"] },
  autossimo: { label: "Autossimo", modes: ["immat", "reference"] },
  amp: { label: "Auto Moto Pneus", modes: ["tires", "reference"] },
  fridayparts: { label: "FridayParts", modes: ["reference"] },
};
const MODE_LABELS = { immat: "Immatriculation", reference: "Référence", tires: "Pneus (dimension)" };

const money = (value) =>
  value == null ? "—" : `${Number(value).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

function errorText(err) {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === "object") return `${detail.message} (${detail.code})`;
  if (typeof detail === "string") return detail;
  return "Erreur réseau ou serveur indisponible.";
}

function StatusCard({ status, onCheck, checking }) {
  const meta = SUPPLIERS[status.supplier] || { label: status.supplier };
  const ok = ["authenticated", "public_available"].includes(status.session);
  return (
    <div className="bg-white border-2 border-black p-4" data-testid={`supplier-status-${status.supplier}`}>
      <div className="flex items-center justify-between gap-2">
        <strong>{meta.label}</strong>
        <span className={`text-xs font-bold px-2 py-0.5 border-2 ${ok ? "border-[#00E676] text-[#00A050]" : "border-zinc-300 text-zinc-500"}`}>
          {!status.configured ? "Non configuré" : ok ? (status.access_mode === "public_catalog" ? "Catalogue accessible" : "Connecté") : "Non connecté"}
        </span>
      </div>
      {status.access_mode === "public_catalog" && <p className="text-xs text-zinc-500 mt-2">Catalogue public, sans connexion au compte professionnel.</p>}
      {status.last_error && (
        <p className="text-xs text-red-600 mt-2">{status.last_error.message} ({status.last_error.code})</p>
      )}
      {status.last_success_at && <p className="text-xs text-zinc-500 mt-1">Dernier succès : {new Date(status.last_success_at).toLocaleString("fr-FR")}</p>}
      <button className="btn-secondary !py-1 !px-3 !text-xs mt-3" disabled={!status.configured || checking} onClick={() => onCheck(status.supplier)}>
        {checking ? "Vérification…" : "Vérifier la connexion"}
      </button>
    </div>
  );
}

function ProductsTable({ products }) {
  if (!products?.length) return null;
  return (
    <div className="bg-white border-2 border-black overflow-x-auto mt-4">
      <table className="w-full text-sm">
        <thead className="bg-black text-white"><tr>
          {["", "Désignation", "Marque / Réf.", "Prix achat", "Prix public", "Disponibilité"].map((h) => (
            <th key={h} className="text-left p-2 text-xs uppercase tracking-wider">{h}</th>
          ))}
        </tr></thead>
        <tbody className="divide-y divide-zinc-100">
          {products.map((p) => (
            <tr key={p.id}>
              <td className="p-2 w-16">{p.image ? <img src={p.image} alt="" className="w-14 h-14 object-contain" loading="lazy" /> : <span className="text-xs text-zinc-400">Sans image</span>}</td>
              <td className="p-2">
                <div className="font-semibold">{p.designation}</div>
                <div className="text-xs text-zinc-500">{[p.category, p.sub_category].filter(Boolean).join(" · ")} · source : {p.source}</div>
              </td>
              <td className="p-2 mono text-xs">{p.equipementier || "—"}<br />{p.reference || "—"}{p.reference_label && <div className="text-zinc-500">{p.reference_label}</div>}</td>
              <td className="p-2 whitespace-nowrap">{money(p.prix_achat)} {p.prix_achat != null && <span className="text-xs text-zinc-500">{p.prix_achat_basis || "HT/TTC non précisé"}</span>}</td>
              <td className="p-2 whitespace-nowrap">
                {money(p.prix_vente_conseille)} {p.prix_vente_conseille != null && <span className="text-xs text-zinc-500">{p.prix_vente_conseille_basis || "HT/TTC non précisé"}</span>}
                {p.prix_vente_source === "calcule_depuis_remise" && <div className="text-xs text-zinc-500">calculé depuis la remise</div>}
                {p.prix_vente_conseille_basis === "TTC" && p.tva_taux != null && <div className="text-xs text-zinc-500">TVA fournisseur {p.tva_taux} %</div>}
                {p.price_note && <div className="text-xs text-zinc-500 whitespace-normal max-w-xs">{p.price_note}</div>}
              </td>
              <td className="p-2 text-xs">{p.dispo_label}{p.stock != null ? ` (stock ${p.stock})` : ""}{p.delai ? ` · délai ${p.delai}` : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function SupplierSearchPanel() {
  const [statuses, setStatuses] = useState([]);
  const [checking, setChecking] = useState(null);
  const [supplier, setSupplier] = useState("ferebam");
  const [mode, setMode] = useState("immat");
  const [query, setQuery] = useState("");
  const [tyre, setTyre] = useState({ width: "", height: "", diameter: "", load_index: "", speed_index: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [parts, setParts] = useState(null);

  const loadStatus = () => api.get("/admin/suppliers").then((r) => setStatuses(r.data.suppliers || [])).catch(() => {});
  useEffect(() => { loadStatus(); }, []);

  const check = async (name) => {
    setChecking(name);
    try { await api.post(`/admin/suppliers/${name}/check`); }
    catch (err) { setError(errorText(err)); }
    finally { setChecking(null); loadStatus(); }
  };

  const chooseSupplier = (name) => {
    setSupplier(name); setMode(SUPPLIERS[name].modes[0]); setResult(null); setParts(null); setError("");
  };

  const search = async (e, refresh = false) => {
    e?.preventDefault();
    setLoading(true); setError(""); setResult(null); setParts(null);
    try {
      const { data } = mode === "tires"
        ? await api.get("/admin/suppliers/amp/tires", { params: { ...tyre, refresh } })
        : await api.get(`/admin/suppliers/${supplier}/${mode}`, { params: { q: query, refresh } });
      setResult(data);
    } catch (err) { setError(errorText(err)); }
    finally { setLoading(false); loadStatus(); }
  };

  const openRepere = async (url) => {
    setLoading(true); setError(""); setParts(null);
    try {
      const { data } = await api.get(`/admin/suppliers/${supplier}/parts`, { params: { url } });
      setParts(data);
    } catch (err) { setError(errorText(err)); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="suppliers-tab">
      <h2 className="font-display font-extrabold text-2xl mb-1">Recherche fournisseurs</h2>
      <p className="text-sm text-zinc-500 mb-4">Usage interne pour préparer les devis. Lecture seule : aucun panier ni commande fournisseur.</p>

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        {statuses.map((s) => <StatusCard key={s.supplier} status={s} onCheck={check} checking={checking === s.supplier} />)}
      </div>

      <form onSubmit={search} className="bg-white border-2 border-black p-4 space-y-3">
        <div className="flex flex-wrap gap-2">
          {Object.entries(SUPPLIERS).map(([name, meta]) => (
            <button type="button" key={name} onClick={() => chooseSupplier(name)}
              className={`px-3 py-1 text-sm font-bold border-2 border-black ${supplier === name ? "bg-black text-white" : "bg-white"}`}>
              {meta.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          {SUPPLIERS[supplier].modes.map((m) => (
            <label key={m} className="text-sm flex items-center gap-1">
              <input type="radio" name="supplier-mode" checked={mode === m} onChange={() => { setMode(m); setResult(null); setParts(null); }} />
              {supplier === "fridayparts" ? "Référence, marque ou modèle d’engin" : MODE_LABELS[m]}
            </label>
          ))}
        </div>
        {mode === "tires" ? (
          <div className="flex flex-wrap gap-2 items-end">
            {[["width", "Largeur", "315"], ["height", "Hauteur", "35"], ["diameter", "Diamètre", "20"], ["load_index", "Ind. charge", "110"], ["speed_index", "Ind. vitesse", "Y"]].map(([key, label, ph]) => (
              <label key={key} className="text-xs uppercase tracking-wider">{label}
                <input className="block border-2 border-black px-2 py-1 w-24 text-sm" value={tyre[key]} placeholder={ph}
                  onChange={(e) => setTyre({ ...tyre, [key]: e.target.value })} required={["width", "height", "diameter"].includes(key)} />
              </label>
            ))}
          </div>
        ) : (
          <input className="border-2 border-black px-3 py-2 w-full max-w-md" value={query} required minLength={3}
            placeholder={mode === "immat" ? "AB-123-CD" : supplier === "fridayparts" ? "Ex. D902, Kubota, 16851-52033" : "Référence pièce"} onChange={(e) => setQuery(e.target.value)} />
        )}
        <div className="flex gap-2">
          <button className="btn-primary" type="submit" disabled={loading}>{loading ? "Recherche…" : "Rechercher"}</button>
          {result?.cached && <button type="button" className="btn-secondary" onClick={() => search(null, true)}>Actualiser (résultat en cache)</button>}
        </div>
      </form>

      {error && <div className="mt-4 border-2 border-red-500 bg-red-50 text-red-700 p-3 text-sm" role="alert">{error}</div>}
      {result?.notice && <p className="mt-4 text-sm text-zinc-600">{result.notice}</p>}

      {result && result.vehicle !== undefined && (
        <div className="mt-4 bg-white border-2 border-black p-4">
          {result.vehicle ? (
            <>
              <h3 className="font-bold">{result.vehicle.name || [result.vehicle.brand, result.vehicle.model, result.vehicle.engine].filter(Boolean).join(" ")}</h3>
              <div className="grid md:grid-cols-3 gap-3 mt-3">
                {result.categories.map((c) => (
                  <div key={c.name}>
                    <div className="text-xs uppercase tracking-wider font-bold">{c.name}</div>
                    {c.reperes.map((r) => (
                      <button key={r.url} type="button" className="block text-left text-sm underline" onClick={() => openRepere(r.url)}>{r.name}</button>
                    ))}
                  </div>
                ))}
              </div>
            </>
          ) : <p className="text-sm">Véhicule non trouvé chez ce fournisseur.</p>}
        </div>
      )}

      {result?.products && (result.products.length
        ? <ProductsTable products={result.products} />
        : <p className="mt-4 text-sm">Aucun résultat chez ce fournisseur.</p>)}
      {parts && (parts.products.length
        ? <ProductsTable products={parts.products} />
        : <p className="mt-4 text-sm">Aucune pièce pour ce repère.</p>)}
    </div>
  );
}
